import React, { useEffect, useState } from 'react';

import { useLocation, useParams, Link } from 'react-router-dom';
import LoadingCircle from 'components/ui/LoadingCircle';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import SearchField from 'components/v2/SearchField';
import { Menu } from '@headlessui/react';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import Pagination from 'components/v2/PaginationV2';
import moment from 'moment';
import useModal from 'hooks/useModal';
import {
  useDeleteReport,
  useGroups,
  useReports,
  useToggleFavoriteReport,
  useUpdateReport,
} from 'hooks/unifiedview/useReports';
import Alert from 'components/ui/Alert';
import useDebounce from 'hooks/useDebounce';
import Tooltip from 'components/ui/Tooltip';
import { CheckIcon } from '@heroicons/react/20/solid';
import SessionStore from 'stores/SessionStore';
import {
  Table,
  TableFooter,
  TableRow,
  TableCell,
  TableBody,
  TableHeader,
  TableHead,
  SortableColumnHeader,
} from 'components/ui/table';

import { useTranslation } from 'react-i18next';
import Favorite from './Favorite';
import RenameReportModal from './RenameReportModal';
import DeleteReportModal from './DeleteReportModal';
import Layout from './Layout';
import Breadcrumbs from './Breadcrumbs';
import { SIDEBAR_STATUSES, VIEWS } from './constants';
import { firstLetterTitlization } from '../../../utils/localization/utils';

const DEFAULT_REPORTS_PER_PAGE = 15;

export default function Reports() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const {
    visible: renameModalVisible,
    show: showRenameModal,
    close: closeRenameModal,
  } = useModal();
  const {
    visible: confirmDeleteModalVisible,
    show: showConfirmDeleteModal,
    close: closeConfirmDeleteModal,
  } = useModal();
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  const groupId = params.groupId || '';
  const favorites = location.pathname.includes('favorites');
  const isMyReports = location.pathname.includes('my-reports');
  const isGroups = location.pathname.includes('groups');
  const populatedFieldsSortable = favorites || isMyReports || isGroups;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_REPORTS_PER_PAGE);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  function toggleFavorite(report) {
    return function (e) {
      e?.preventDefault();
      toggleFavoriteMutation.mutate({
        id: report._id,
        isFavorite: !report.isFavorite,
      });
    };
  }
  function handleSearch(search) {
    setSearch(search);
  }

  function getView() {
    if (favorites) {
      return VIEWS.FAVORITES.name;
    }

    if (isMyReports) {
      return VIEWS.MY_REPORTS.name;
    }

    if (!groupId) {
      return VIEWS.ALL_REPORTS.name;
    }

    return VIEWS.GROUPS.name;
  }

  const filters = {
    groupId,
    favorites,
    search: debouncedSearch,
    page,
    perPage,
    sortKey,
    sortOrder,
    isMyReports,
    lean: !populatedFieldsSortable,
  };

  const { data, error, isLoading, isError, isSuccess } = useReports({
    filters,
  });

  const { mutation } = useUpdateReport({
    filters,
    onSuccess: handleSaveSuccess,
  });

  const { mutation: toggleFavoriteMutation } = useToggleFavoriteReport({
    filters,
    onSuccess: () => {
      checkPageOver(data.total - 1);
    },
  });

  const { mutation: deleteMutation } = useDeleteReport({
    filters,
    onSuccess: () => {
      handleDeleteSuccess();
      checkPageOver(data.total - 1);
    },
  });

  const { getGroupById } = useGroups();

  useEffect(() => {
    checkPageOver();
  }, [data]);

  const checkPageOver = (itemsCount) => {
    if (!itemsCount) {
      itemsCount = data?.total;
    }

    const totalPages = Math.ceil(itemsCount / perPage);

    if (page > totalPages) {
      setPage(totalPages > 0 ? totalPages : 1);
    }
  };

  function handleRename(report) {
    return function () {
      setSelectedReport(report);
      showRenameModal();
    };
  }

  function handleDelete(report) {
    deleteMutation.mutate({ ...report, id: report._id });
  }

  function handleConfirmDelete(report) {
    return function () {
      setSelectedReport(report);
      showConfirmDeleteModal();
    };
  }

  function handleDeleteSuccess() {
    closeConfirmDeleteModal();
  }

  function handleSave(report) {
    mutation.mutate(report);
  }

  function handlePageClick(page) {
    setPage(parseInt(page, 10));
  }

  function handleSaveSuccess() {
    closeRenameModal();
  }

  const handleSort = (sortConfig) => {
    setSortKey(sortConfig?.key);
    setSortOrder(sortConfig?.order);
  };

  const getGroupFullName = (group) => {
    if (!group) {
      return '';
    }

    const parentGroup = getGroupById(group.parent);

    return parentGroup ? `${parentGroup.name} - ${group.name}` : group.name;
  };

  const formatUsername = (user) => {
    if (!user) return '';

    return `${user.first_name} ${user.last_name}`;
  };

  function renderReports() {
    return (
      <>
        <DeleteReportModal
          report={selectedReport}
          isOpen={confirmDeleteModalVisible}
          onClose={closeConfirmDeleteModal}
          onConfirm={handleDelete}
        />
        <RenameReportModal
          report={selectedReport}
          isOpen={renameModalVisible}
          onClose={closeRenameModal}
          onSave={handleSave}
        />

        <Breadcrumbs groupId={groupId} />
        <SearchField
          dataTestId="SearchField-fb053362"
          className="mb-4"
          onChange={handleSearch}
          value={search}
        />
        <div
          data-testid="div-e877f3db"
          className="table-wrapper overflow-auto relative"
          style={{ maxHeight: 'calc(100dvh - 230px)' }}
        >
          <Table dataTestId="Table-46c76356" className="w-full">
            <TableHeader
              dataTestId="TableHeader-f17bd990"
              className="sticky top-0 bg-gray-50"
              onSortChange={handleSort}
            >
              <TableRow dataTestId="TableRow-45709afe">
                <SortableColumnHeader
                  dataTestId="SortableColumnHeader-034720f5"
                  sortKey="name"
                >
                  {t('NAME')}
                </SortableColumnHeader>
                <SortableColumnHeader
                  dataTestId="SortableColumnHeader-7ca8ef02"
                  className=""
                >
                  {t('DESCRIPTION')}
                </SortableColumnHeader>
                <TableHead dataTestId="SortableColumnHeader-7ca8ef03">
                  {t('FILTER_TYPE')}
                </TableHead>
                {populatedFieldsSortable ? (
                  <SortableColumnHeader
                    dataTestId="SortableColumnHeader-0159e5bd"
                    sortKey="groupId.name"
                    className="w-32"
                  >
                    {firstLetterTitlization(t('GROUP', { count: 1 }))}
                  </SortableColumnHeader>
                ) : (
                  <TableHead dataTestId="TableHead-e0446590" className="w-32">
                    {firstLetterTitlization(t('GROUP', { count: 1 }))}
                  </TableHead>
                )}
                {populatedFieldsSortable ? (
                  <SortableColumnHeader
                    dataTestId="SortableColumnHeader-51475ab4"
                    sortKey="creator_id.optimized_name"
                    className="w-32"
                  >
                    {t('CREATED_BY')}
                  </SortableColumnHeader>
                ) : (
                  <TableHead dataTestId="TableHead-f1960b0d" className="w-32">
                    {t('CREATED_BY')}
                  </TableHead>
                )}
                <SortableColumnHeader
                  dataTestId="SortableColumnHeader-d4700c84"
                  sortKey="created_at"
                  className="w-32"
                >
                  {t('CREATED_AT')}
                </SortableColumnHeader>
                {populatedFieldsSortable ? (
                  <SortableColumnHeader
                    dataTestId="SortableColumnHeader-0ac09024"
                    sortKey="update_author.optimized_name"
                    className="w-32"
                  >
                    {t('UPDATED_BY')}
                  </SortableColumnHeader>
                ) : (
                  <TableHead dataTestId="TableHead-9cc2bf30" className="w-32">
                    {t('UPDATED_BY')}
                  </TableHead>
                )}
                <SortableColumnHeader
                  dataTestId="SortableColumnHeader-184dc96a"
                  sortKey="modified_at"
                  className="w-32"
                >
                  {t('UPDATED_AT')}
                </SortableColumnHeader>
                <TableHead
                  dataTestId="TableHead-39eea8c0"
                  sortKey="shared"
                  className="w-20"
                >
                  {t('PUBLIC')}?
                </TableHead>
                <SortableColumnHeader
                  dataTestId="SortableColumnHeader-a1bbda62"
                  className="text-right w-12"
                >
                  <span data-testid="span-9cd069aa" className="sr-only">
                    {t('ACTIONS')}
                  </span>
                </SortableColumnHeader>
              </TableRow>
            </TableHeader>
            <TableBody dataTestId="TableBody-b33a54be">
              {isLoading && (
                <TableRow dataTestId="TableRow-e570ef23">
                  <TableCell dataTestId="TableCell-934406f2" colSpan="100%">
                    <span data-testid="span-2537e390" className="sr-only">
                      {t('LOADING')}...
                    </span>
                    <LoadingCircle dataTestId="LoadingCircle-4a7af0e9" />
                  </TableCell>
                </TableRow>
              )}

              {!data?.items?.length && !isLoading ? (
                <TableRow dataTestId="TableRow-9e9d117d">
                  <TableCell
                    dataTestId="TableCell-af6fb912"
                    colSpan="100%"
                    className="text-center"
                  >
                    <span data-testid="span-23822ded" className="">
                      {t('NO_REPORTS_FOUND')}
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                data?.items?.map((report, index) => (
                  <TableRow
                    dataTestId={`TableRow-0e281ebb-${index}`}
                    key={report._id}
                  >
                    <TableCell
                      dataTestId={`TableCell-04570e2c-${index}`}
                      className="w-64"
                    >
                      {report.name && (
                        <Tooltip
                          dataTestId={`Tooltip-b410936e-${index}`}
                          id={report.name}
                          content={report.name}
                          tooltipClassName="w-auto"
                        >
                          <p
                            data-testid={`p-025dc39e-${index}`}
                            className="line-clamp-2 w-64 whitespace-normal"
                          >
                            <Link to={`/unifiedview/reports/${report._id}`}>
                              {report.name}
                            </Link>
                          </p>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-f6c6735a-${index}`}
                      className=""
                    >
                      {report.description && (
                        <Tooltip
                          dataTestId={`Tooltip-999cf0a1-${index}`}
                          id={report._id}
                          content={report.description}
                          tooltipClassName="w-auto"
                        >
                          <p
                            data-testid={`p-8a449fc1-${index}`}
                            className="line-clamp-2 w-64 whitespace-normal"
                          >
                            {report.description}
                          </p>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell dataTestId={`TableCell-filter-type-${index}`}>
                      {report.filterType}
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-2efe77a0-${index}`}
                      className="w-32"
                    >
                      <Tooltip
                        dataTestId={`Tooltip-0610e973-${index}`}
                        id={report._id}
                        content={getGroupFullName(report.groupId)}
                        tooltipClassName="w-auto"
                      >
                        {getGroupFullName(report.groupId)}
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-989a16f5-${index}`}
                      className="w-32"
                      title={report.createdBy}
                    >
                      <Tooltip
                        dataTestId={`Tooltip-265a399c-${index}`}
                        id={report._id}
                        content={formatUsername(report.creator_id)}
                        tooltipClassName="w-auto"
                      >
                        {formatUsername(report.creator_id)}
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-7bbe8080-${index}`}
                      className="w-16"
                    >
                      {report.created_at
                        ? moment(report.created_at).format('MM/DD/YYYY')
                        : ''}
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-5a8cc57c-${index}`}
                      className="w-32 "
                      title={report.updatedBy}
                    >
                      <Tooltip
                        dataTestId={`Tooltip-dc71f4f5-${index}`}
                        id={report._id}
                        content={formatUsername(report.update_author)}
                        tooltipClassName="w-auto"
                      >
                        {formatUsername(report.update_author)}
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-2f53360e-${index}`}
                      className="w-16"
                    >
                      {report.modified_at
                        ? moment(report.modified_at).format('MM/DD/YYYY')
                        : null}
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-08f19eee-${index}`}
                      className="w-8"
                    >
                      {report.shared && (
                        <CheckIcon className="text-green-cm h-5 w-5" />
                      )}
                    </TableCell>
                    <TableCell
                      dataTestId={`TableCell-0dd60a77-${index}`}
                      className="text-right w-20"
                    >
                      <span
                        data-testid={`span-d1d1d754-${index}`}
                        className="sr-only"
                      >
                        Menu
                      </span>
                      <div
                        data-testid={`div-e07d1d7e-${index}`}
                        className="flex items-center space-x-2 justify-end"
                      >
                        <Favorite
                          selected={report.isFavorite}
                          onToggle={toggleFavorite(report)}
                        />
                        <DropdownMenu
                          dataTestId={`DropdownMenu-92ccbbf7-${index}`}
                          useIcon
                        >
                          <Menu.Item
                            data-testid={`MenuItem-154c0ab1-${index}`}
                            key="delete_report"
                          >
                            <DropdownItem
                              dataTestId="deleteReportButton"
                              onClick={handleConfirmDelete(report)}
                              disabled={
                                !SessionStore.isAbleToManageUVReport(report)
                              }
                              disabledText="You do not have permission to delete this report"
                              disabledTextPosition="left"
                            >
                              <span data-testid={`span-5eb6ba44-${index}`}>
                                {t('DELETE')}
                              </span>
                            </DropdownItem>
                          </Menu.Item>
                          <Menu.Item
                            data-testid={`MenuItem-b9e6c469-${index}`}
                            key="rename_report"
                          >
                            <DropdownItem
                              dataTestId="renameReportButton"
                              onClick={handleRename(report)}
                              disabled={
                                !SessionStore.isAbleToManageUVReport(report)
                              }
                              disabledText="You do not have permission to rename this report"
                              disabledTextPosition="left"
                            >
                              <span data-testid={`span-5a780026-${index}`}>
                                {t('RENAME')}
                              </span>
                            </DropdownItem>
                          </Menu.Item>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter
              dataTestId="TableFooter-dff19e13"
              className="sticky bottom-0 bg-gray-50"
            >
              {data?.total > perPage && (
                <TableRow dataTestId="TableRow-c9b23b38" className="border-0">
                  <TableCell dataTestId="TableCell-93b802ce" colSpan="100%">
                    <span data-testid="span-b30993d4" className="sr-only">
                      Pagination
                    </span>
                    <Pagination
                      dataTestId="Pagination-2d73a4cf"
                      page={page}
                      perPage={perPage}
                      total={data?.total}
                      onPageClick={handlePageClick}
                      showCount
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </div>
      </>
    );
  }
  return (
    <Layout
      groupId={groupId}
      activeTab={getView()}
      sidebarDefaultState={SIDEBAR_STATUSES.EXPANDED}
    >
      <div data-testid="div-5b3b1d0f" className="px-4 sm:px-6 lg:px-8">
        {isError && (
          <Alert dataTestId="Alert-8cd6f4cf" type="error" className="my-4">
            {error}
          </Alert>
        )}
        {renderReports()}
      </div>
    </Layout>
  );
}
